import React from 'react'
import styled from '@emotion/styled'
import { useTranslation } from '~/hooks/useTranslation'
import YnsIcon, { Mute, Unmute, Video, VideoOff } from '../../icons/YnsIcon'
import { useCoachChat } from './CoachChatContext'

const HeaderContainer = styled.div`
  flex-shrink: 0;
  background: rgb(28, 111, 41);
  padding: 0.625rem 1.25rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const HeaderTitle = styled.div`
  color: white;
  font-family: roboto, Arial, Helvetica, sans-serif;
  font-size: 1rem;
  font-weight: 500;
  line-height: 3rem;
`

const HeaderActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
`

const HeaderButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  padding: 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:active {
    background: rgba(255, 255, 255, 0.2);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.8);
    outline-offset: 2px;
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`

/**
 * CoachChatHeader component - displays chat header with title and action buttons
 * Based on the candidate chat header but adapted for coach use
 *
 * @param {Object} props
 * @param {Object} props.candidate - The candidate being chatted with
 * @param {string} props.chatMode - Current chat mode (text, audio, video)
 */
const CoachChatHeader = ({ candidate, chatMode }) => {
  const { chatManager, connectionState } = useCoachChat()
  const { t } = useTranslation('coach_chat')

  // Pause control only for video; mute for audio and video
  const showPauseControls = chatMode === 'video'
  const showMuteControls = chatMode === 'audio' || chatMode === 'video'

  // CTS-9777 / CC-UX-012: single toggles reflecting local media state
  const isMuted = connectionState?.audioMuted || false
  const isPaused = connectionState?.videoPaused || false

  const muteLabel = isMuted
    ? t('career_development.unmute_microphone', {
        defaultValue: 'Unmute microphone',
      })
    : t('career_development.mute_microphone', {
        defaultValue: 'Mute microphone',
      })

  const pauseLabel = isPaused
    ? t('career_development.resume_chat', { defaultValue: 'Resume Chat' })
    : t('career_development.pause_chat', { defaultValue: 'Pause Chat' })

  return (
    <HeaderContainer id="chat_header">
      <HeaderTitle>
        {/* Show candidate name if available, otherwise generic title */}
        {candidate
          ? `Chat with ${
              chatManager?.resolveCandidateDisplayName?.(candidate) ??
              candidate.displayName ??
              candidate.username
            }`
          : 'Coach Chat'}
      </HeaderTitle>

      <HeaderActions>
        {/* Single pause toggle for video — match candidate ChatHeader */}
        {showPauseControls && (
          <HeaderButton
            type="button"
            id={isPaused ? 'btn_unpause_chat' : 'btn_pause_chat'}
            onClick={() => {
              if (isPaused) {
                chatManager?.unpauseChat()
              } else {
                chatManager?.pauseChat()
              }
            }}
            title={pauseLabel}
            aria-label={pauseLabel}
            aria-pressed={isPaused}
          >
            {isPaused ? <Video /> : <VideoOff />}
          </HeaderButton>
        )}

        {/* Single mute toggle for audio/video — CTS-9777 / CC-UX-012 */}
        {showMuteControls && (
          <HeaderButton
            type="button"
            id={isMuted ? 'btn_unmute_chat' : 'btn_mute_chat'}
            onClick={() => {
              if (isMuted) {
                chatManager?.unmuteChat()
              } else {
                chatManager?.muteChat()
              }
            }}
            title={muteLabel}
            aria-label={muteLabel}
            aria-pressed={isMuted}
          >
            {isMuted ? <Unmute /> : <Mute />}
          </HeaderButton>
        )}

        {/* Always show end chat button */}
        <HeaderButton
          type="button"
          onClick={() =>
            chatManager?.endChat(
              candidate?.id ||
                candidate?.candidateEasyrtcid ||
                candidate?.easyrtcid,
            )
          }
          title={t('career_development.end_chat', {
            defaultValue: 'End Chat Now',
          })}
          aria-label={t('career_development.end_chat', {
            defaultValue: 'End Chat Now',
          })}
        >
          <YnsIcon name="x" className="h-4 w-4" />
        </HeaderButton>
      </HeaderActions>
    </HeaderContainer>
  )
}

export default CoachChatHeader
